/**
 * Een menu dat niet door zijn kolom wordt afgeknipt.
 *
 * Het Acties-menu stond als `position: absolute` in de blokkop. Die kop
 * zit in de middenkolom, en die kolom heeft `overflow: hidden` met een
 * eigen scrollgebied eronder — dus werd het menu bij de kolomrand
 * doorgesneden en was alleen de rechterhelft te zien (JP, punt 10 van
 * 22-09).
 *
 * Dit component hangt het menu in een portal aan `document.body` en zet
 * het met `position: fixed` onder de knop. Daarmee kan geen enkele
 * ouder het nog afknippen. Drie dingen doet het zelf:
 *
 * - **Rechts uitlijnen als het niet past.** Standaard begint het menu
 *   aan de linkerkant van de knop; steekt het dan buiten het venster,
 *   dan schuift het naar links tot het past, met 8 px lucht tot de rand.
 * - **Boven de knop als er onder geen ruimte is.** Zelfde idee, maar
 *   verticaal.
 * - **Meebewegen met scrollen en verschalen.** Het menu sluit dan; een
 *   zwevend menu dat naast zijn knop blijft hangen is erger dan een
 *   menu dat dichtgaat.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

interface Props {
  /** De knop waar het menu onder hangt. */
  anker: HTMLElement | null;
  open: boolean;
  sluit: () => void;
  children: React.ReactNode;
  /** Voor de schermtoets: komt op het zwevende element te staan. */
  testAttribuut?: string;
}

const RAND = 8;

export function Zwevendmenu({
  anker,
  open,
  sluit,
  children,
  testAttribuut,
}: Props) {
  const lijst = useRef<HTMLDivElement | null>(null);
  const [plek, setPlek] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!open || !anker) {
      setPlek(null);
      return;
    }
    const knop = anker.getBoundingClientRect();
    const menu = lijst.current?.getBoundingClientRect();
    const breedte = menu?.width ?? 280;
    const hoogte = menu?.height ?? 200;

    // Rechts uitlijnen met de knop; past dat niet, dan schuift hij naar
    // binnen tot hij past.
    let left = knop.right - breedte;
    left = Math.min(left, window.innerWidth - breedte - RAND);
    left = Math.max(RAND, left);

    let top = knop.bottom + 4;
    if (top + hoogte > window.innerHeight - RAND) {
      const boven = knop.top - hoogte - 4;
      top = boven > RAND ? boven : Math.max(RAND, window.innerHeight - hoogte - RAND);
    }
    setPlek({ top, left });
  }, [open, anker, children]);

  useEffect(() => {
    if (!open) return;
    const dicht = () => sluit();
    // `true`: ook scrollen binnen een kolom telt, niet alleen het
    // venster. Zonder de capture-fase blijft het menu hangen zodra de
    // middenkolom onder hem doorschuift.
    window.addEventListener("scroll", dicht, true);
    window.addEventListener("resize", dicht);
    return () => {
      window.removeEventListener("scroll", dicht, true);
      window.removeEventListener("resize", dicht);
    };
  }, [open, sluit]);

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      ref={lijst}
      className="wp-actiemenu-lijst wp-actiemenu-zwevend"
      role="menu"
      data-zwevend={testAttribuut ?? "acties"}
      style={{
        top: plek?.top ?? -9999,
        left: plek?.left ?? -9999,
        // Tot de meting klaar is staat hij buiten beeld in plaats van
        // even links boven in de hoek te knipperen.
        visibility: plek ? "visible" : "hidden",
      }}
    >
      {children}
    </div>,
    document.body,
  );
}
